/**
 * Pure math helpers for the grouped custom-meal editor (CustomMealView).
 * Kept side-effect free so unit changes / totals are unit-testable.
 */

// Sum the parent macros from the structured child ingredients.
export function computeStructuredTotals(list) {
  var totals = { calories: 0, protein: 0, carbs: 0, fat: 0 };
  (list || []).forEach(function (i) {
    if (i && i.structured) {
      totals.calories += i.calories || 0;
      totals.protein += i.protein || 0;
      totals.carbs += i.carbs || 0;
      totals.fat += i.fat || 0;
    }
  });
  return {
    calories: Math.round(totals.calories),
    protein: Math.round(totals.protein),
    carbs: Math.round(totals.carbs),
    fat: Math.round(totals.fat),
  };
}

/**
 * FatSecret-aware unit change for one structured ingredient.
 * `option` is a serving_options entry from GET /log-food/food/{id}: it carries
 * the per-ONE-unit `macros`, the `size`, and the serving identity/measurement
 * metadata. The ingredient's numeric quantity is preserved; macros are
 * recomputed as option.macros * quantity (NEVER a label-only change), and the
 * full serving identity is retained so a later reopen/edit stays consistent.
 *
 * Returns { ingredients, totals }. When `option` is missing/invalid the input
 * is returned unchanged (caller disables switching in that case).
 */
export function applyUnitChange(ingredients, id, option) {
  if (!option || !option.unit || !option.macros) {
    return { ingredients: ingredients, totals: computeStructuredTotals(ingredients) };
  }
  var updated = (ingredients || []).map(function (i) {
    if (i.id !== id) return i;
    if (!i.food_id) return i;
    var qty = i.quantity || 1;
    var per = {
      calories: option.macros.calories || 0,
      protein: option.macros.protein || 0,
      carbs: option.macros.carbs || 0,
      fat: option.macros.fat || 0,
    };
    return Object.assign({}, i, {
      unit: option.unit,
      per_unit: per,
      calories: Math.round(per.calories * qty),
      protein: Math.round(per.protein * qty),
      carbs: Math.round(per.carbs * qty),
      fat: Math.round(per.fat * qty),
      text: qty + ' ' + option.unit + ' ' + i.name,
      serving_id: option.serving_id != null ? option.serving_id : (i.serving_id || null),
      serving_size: option.size != null ? option.size : (i.serving_size || null),
      serving_description: option.description || i.serving_description || null,
      measurement_description: option.measurement_description || i.measurement_description || null,
      metric_serving_amount: option.metric_serving_amount != null ? option.metric_serving_amount : (i.metric_serving_amount != null ? i.metric_serving_amount : null),
      metric_serving_unit: option.metric_serving_unit || i.metric_serving_unit || null,
    });
  });
  return { ingredients: updated, totals: computeStructuredTotals(updated) };
}
